/**
 * Revenue Per Staff Chart Component
 *
 * Area chart showing revenue per staff comparison between current and previous year.
 * Uses Recharts AreaChart with custom tooltip and year-over-year comparison.
 */

import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, TooltipProps } from 'recharts';
import styles from './RevenuePerStaffChart.module.scss';

// Chart colors matching the design
const CHART_COLORS = {
    current: '#5E8677', // Dark green for 2025
    previous: '#BDD2CA' // Light green for 2024
} as const;

// Sample data structure
interface RevenuePerStaffData {
    month: string;
    current: number; // 2025 data
    previous: number; // 2024 data
}

// Mock data for the chart (Jan-Sep comparison) - Based on design showing $55,614 total
const revenuePerStaffData: RevenuePerStaffData[] = [
    { month: 'Jan', current: 5000, previous: 7500 },
    { month: 'Feb', current: 6200, previous: 8200 },
    { month: 'Mar', current: 7146, previous: 6678 },
    { month: 'Apr', current: 6800, previous: 7900 },
    { month: 'May', current: 5900, previous: 8500 },
    { month: 'Jun', current: 5200, previous: 8800 },
    { month: 'Jul', current: 6100, previous: 9200 },
    { month: 'Aug', current: 6800, previous: 9500 },
    { month: 'Sep', current: 6466, previous: 9000 }
];

// Custom Tooltip Component
const CustomTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    const currentValue = payload.find(p => p.dataKey === 'current')?.value;
    const previousValue = payload.find(p => p.dataKey === 'previous')?.value;

    return (
        <div className={styles.customTooltip}>
            <div className={styles.tooltipContent}>
                {currentValue && (
                    <div className={styles.tooltipItem}>
                        <div
                            className={styles.colorDot}
                            style={{ backgroundColor: CHART_COLORS.current }}
                        />
                        <span className={styles.tooltipLabel}>{label} 2025</span>
                        <span className={styles.tooltipValue}>${currentValue.toLocaleString()}</span>
                    </div>
                )}
                {previousValue && (
                    <div className={styles.tooltipItem}>
                        <div
                            className={styles.colorDot}
                            style={{ backgroundColor: CHART_COLORS.previous }}
                        />
                        <span className={styles.tooltipLabel}>{label} 2024</span>
                        <span className={styles.tooltipValue}>${previousValue.toLocaleString()}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

// Format Y-axis values
const formatYAxisValue = (value: number): string => {
    if (value >= 1000) {
        return `$${value / 1000}k`;
    }
    return `$${value}`;
};

// Calculate percentage change
const calculateChange = (): { value: string; isPositive: boolean } => {
    const currentTotal = revenuePerStaffData.reduce((sum, item) => sum + item.current, 0);
    const previousTotal = revenuePerStaffData.reduce((sum, item) => sum + item.previous, 0);

    if (previousTotal === 0) return { value: '+0%', isPositive: true };

    const change = ((currentTotal - previousTotal) / previousTotal) * 100;
    const isPositive = change >= 0;

    return {
        value: `${isPositive ? '+' : ''}${Math.round(change)}%`,
        isPositive
    };
};

export const RevenuePerStaffChart: React.FC = () => {
    const currentTotal = revenuePerStaffData.reduce((sum, item) => sum + item.current, 0);
    const change = calculateChange();

    return (
        <div className={styles.chartContainer}>
            {/* Chart Header */}
            <div className={styles.chartHeader}>
                <h3 className={styles.chartTitle}>Revenue per staff</h3>
                <div className={styles.chartMetrics}>
                    <div className={styles.mainValue}>${currentTotal.toLocaleString()}</div>
                    <div className={`${styles.changeValue} ${change.isPositive ? styles.positive : styles.negative}`}>
                        {change.value}
                    </div>
                </div>
            </div>

            {/* Chart Area */}
            <div className={styles.chartWrapper}>
                <ResponsiveContainer
                    width="100%"
                    height={180}
                >
                    <AreaChart
                        data={revenuePerStaffData}
                        margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
                    >
                        <CartesianGrid
                            strokeDasharray="1 1"
                            horizontal={true}
                            vertical={false}
                            stroke="#f0f0f0"
                        />
                        <XAxis
                            dataKey="month"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 11, fill: '#999' }}
                        />
                        <YAxis
                            tickFormatter={formatYAxisValue}
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 11, fill: '#999' }}
                            width={35}
                        />
                        <Tooltip content={<CustomTooltip />} />

                        {/* Previous Year Area (2024) */}
                        <Area
                            type="monotone"
                            dataKey="previous"
                            stroke={CHART_COLORS.previous}
                            fill={CHART_COLORS.previous}
                            fillOpacity={0.1}
                            strokeWidth={2}
                            strokeDasharray="4 4"
                            dot={false}
                            activeDot={{ r: 4, fill: CHART_COLORS.previous, strokeWidth: 0 }}
                        />

                        {/* Current Year Area (2025) */}
                        <Area
                            type="monotone"
                            dataKey="current"
                            stroke={CHART_COLORS.current}
                            fill={CHART_COLORS.current}
                            fillOpacity={0.15}
                            strokeWidth={2}
                            dot={false}
                            activeDot={{ r: 4, fill: CHART_COLORS.current, strokeWidth: 0 }}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>

            {/* Chart Legend */}
            <div className={styles.chartLegend}>
                <div className={styles.legendItem}>
                    <div
                        className={styles.legendLine}
                        style={{ backgroundColor: CHART_COLORS.current }}
                    />
                    <span className={styles.legendLabel}>Jan 1-Sep 18, 2025</span>
                </div>
                <div className={styles.legendItem}>
                    <div
                        className={styles.legendLine}
                        style={{
                            backgroundColor: CHART_COLORS.previous,
                            backgroundImage:
                                'repeating-linear-gradient(90deg, transparent, transparent 3px, white 3px, white 6px)'
                        }}
                    />
                    <span className={styles.legendLabel}>Jan 1-Sep 18, 2024</span>
                </div>
            </div>
        </div>
    );
};
